/**
 * Guest App Sound Notification Service
 * Uses Web Audio API to generate distinct notification sounds.
 * Consent is stored in localStorage so guests only approve once per session.
 */

const CONSENT_KEY = 'gcxp_guest_sound_consent';

export type GuestSoundType =
  | 'notification'      // generic update sound
  | 'message-received'  // new message from hotel
  | 'request-sent'      // guest request submitted
  | 'order-sent'        // order placed
  | 'order-ready'       // order is ready for pickup/delivery
  | 'request-ready';    // request has been fulfilled

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new AudioContext();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
}

// ── Consent management ──────────────────────────────

export function hasSoundConsent(): boolean {
  return localStorage.getItem(CONSENT_KEY) === 'granted';
}

export function grantSoundConsent(): void {
  localStorage.setItem(CONSENT_KEY, 'granted');
  // Unlock audio context with a silent tone
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.01);
  } catch { /* ignore */ }
}

export function revokeSoundConsent(): void {
  localStorage.removeItem(CONSENT_KEY);
}

// ── Sound generators ────────────────────────────────

function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3
) {
  if (!hasSoundConsent()) return;
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.value = frequency;

    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch (err) {
    console.warn('Sound playback failed:', err);
  }
}

function playChime(frequencies: number[], noteDuration: number, gap: number, volume: number = 0.3) {
  if (!hasSoundConsent()) return;
  try {
    const ctx = getAudioContext();
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const startTime = ctx.currentTime + i * (noteDuration + gap);

      osc.type = 'sine';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(volume, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + noteDuration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + noteDuration);
    });
  } catch (err) {
    console.warn('Sound playback failed:', err);
  }
}

// ── Public API ──────────────────────────────────────

export function playSound(sound: GuestSoundType): void {
  if (!hasSoundConsent()) return;

  switch (sound) {
    case 'notification':
      // Soft update tone (C5)
      playTone(523, 0.25, 'sine', 0.2);
      break;

    case 'message-received':
      // Chat bubble: quick rising two-note (E5 → G5)
      playChime([659, 784], 0.08, 0.03, 0.3);
      break;

    case 'request-sent':
      // Confirmation: ascending two-note (C5 → E5)
      playChime([523, 659], 0.1, 0.04, 0.2);
      break;

    case 'order-sent':
      // Confirmation: ascending three-note (C5 → E5 → G5)
      playChime([523, 659, 784], 0.1, 0.04, 0.2);
      break;

    case 'order-ready':
      // Happy chime: descending then up (G5 → E5 → C6)
      playChime([784, 659, 1047], 0.12, 0.05, 0.35);
      break;

    case 'request-ready':
      // Success: rising two-note (E5 → A5)
      playChime([659, 880], 0.15, 0.05, 0.3);
      break;
  }
}
